import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// /en/mfa-legalisation — EN hub (Phase P-MFA-LEGALISATION)
import { createFileRoute, Link } from "@tanstack/react-router";
import { MFA_COUNTRIES } from "@/data/mfa-legalisation-countries";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { Stamp, Globe2, Phone, MessageCircle, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/mfa-legalisation/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("mfa", "en", "https://nyclanguageinstitute.com/en/mfa-legalisation") }),
  head: ({ loaderData }: any) => {
    const thPath = "/mfa-legalisation";
    const enPath = "/en/mfa-legalisation";
    const title = `MFA + Embassy Legalisation Thailand — ${MFA_COUNTRIES.length}+ Non-Apostille Countries | NYC`;
    const desc = `Thai MFA (Department of Consular Affairs) + embassy consular legalisation for non-Hague countries — China, UAE, Saudi Arabia, Vietnam, Taiwan, Iran, Egypt and more. Sworn translators, door-to-door pickup, 5–14 day turnaround. Ask us before you file — it's free.`;
    const url = `${SITE}${enPath}`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/mfa-legalisation", "en") }, { name: "description", content: ctaDesc(desc, "/en/mfa-legalisation", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/mfa-legalisation", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/mfa-legalisation", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "MFA Thailand legalisation, embassy attestation Bangkok, non-apostille countries, consular legalisation, Chaeng Wattana MFA, Department of Consular Affairs" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#collection`,
            name: title, description: desc, url,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: MFA_COUNTRIES.length,
              itemListElement: MFA_COUNTRIES.map((c, i) => ({
                "@type": "ListItem", position: i + 1, name: `Legalisation for ${c.countryEn}`,
                url: `${SITE}/en/mfa-legalisation/${c.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
            name: "MFA + Embassy Consular Legalisation Thailand",
            serviceType: "MFA + Embassy Consular Legalisation Thailand",
            provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            areaServed: "TH",
            offers: { "@type": "Offer", price: "3000", priceCurrency: "THB", description: "MFA legalisation per document" },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { "@type": "Question", name: "What is MFA Legalisation and how is it different from Apostille?",
                acceptedAnswer: { "@type": "Answer", text: "Apostille is a one-step certification used between countries party to the Hague Convention. For non-party destinations (China, UAE, Saudi Arabia, Vietnam, Taiwan) Thai-issued documents need a two-step chain: Thai MFA + the destination country's embassy in Bangkok." } },
              { "@type": "Question", name: "How long does the chain take?",
                acceptedAnswer: { "@type": "Answer", text: "5 days for Laos/Cambodia, up to 14 days for Saudi Arabia/Iran/Egypt — depending on embassy queue and translation language. NYC reports milestones at every step." } },
              { "@type": "Question", name: "What are the total fees?",
                acceptedAnswer: { "@type": "Answer", text: "MFA THB 200/doc + embassy THB 800–3,200/doc depending on destination + sworn translation from THB 800/page. NYC offers an all-in package from THB 3,000 per document including pickup/return courier." } },
              { "@type": "Question", name: "What translation languages are required?",
                acceptedAnswer: { "@type": "Answer", text: "English is accepted for most destinations. Arabic is mandatory for Saudi/Kuwait/Egypt; Persian for Iran; Traditional Chinese for Taiwan; Simplified Chinese for China. NYC employs embassy-registered translators for every language." } },
            ],
          }),
        },
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: MfaHubEn,
});

function MfaHubEn() {
  const byRegionMap: Record<string, string> = {
    "ตะวันออกกลาง": "Middle East", "เอเชียตะวันออก": "East Asia",
    "เอเชียใต้": "South Asia", "เอเชียตะวันออกเฉียงใต้": "Southeast Asia", "แอฟริกา": "Africa",
  };
  const byRegion = MFA_COUNTRIES.reduce<Record<string, typeof MFA_COUNTRIES>>((acc, c) => {
    (acc[c.region] ||= []).push(c); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Stamp className="mr-1 inline h-3 w-3" /> Non-Apostille · MFA + Embassy
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Thai MFA + Embassy Legalisation — {MFA_COUNTRIES.length}+ Non-Apostille Destinations
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            For destinations that are <strong>not</strong> party to the Hague Apostille — China, UAE, Saudi Arabia, Qatar, Kuwait, Vietnam, Taiwan, Iran, Egypt and more.
            NYC delivers the full chain: <strong>sworn translation + notary + MFA Chaeng Wattana + embassy consular stamp</strong> in 5–14 working days.
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How do I legalise a Thai document for a non-Apostille country?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Four steps: (1) sworn translation by a registered translator (2) Notary Public (3) Thai MFA Department of Consular Affairs (Chaeng Wattana) (4) consular legalisation by the destination country's embassy in Bangkok. NYC bundles every step from THB 3,000 per document, end-to-end in 5–14 days.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/apostille" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Globe2 className="h-4 w-4" /> Apostille countries (122)
            </Link>
            <Link to="/en/mfa-legalisation/documents" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Globe2 className="h-4 w-4" /> 6 document families
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Choose your destination</h2>
          <p className="mt-2 text-sm text-muted-foreground">Grouped by region — click for the per-country chain, fees, languages and timeline.</p>
          <div className="mt-8 space-y-10">
            {Object.entries(byRegion).map(([region, list]) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary">{byRegionMap[region] ?? region}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((c) => (
                    <Link
                      key={c.slug}
                      to="/en/mfa-legalisation/$country"
                      params={{ country: c.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="mfa-hub-en"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{c.flag}</span>
                        <div>
                          <div className="font-bold">{c.countryEn}</div>
                          <div className="text-xs text-muted-foreground">{c.totalDays} days · THB {c.embassyFeeTHB.toLocaleString()} embassy fee</div>
                        </div>
                      </div>
                      <div className="mt-3 line-clamp-2 text-xs text-muted-foreground">{c.topDocs.slice(0, 3).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        See chain <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Apostille (Hague — 122 countries)", d: "Single-step MFA only, no embassy needed", to: "/en/apostille" as const },
              { t: "Certified Translation (40+ languages)", d: "Sworn EN/JP/CN/KR/AR/FA translators", to: "/en/translate" as const },
              { t: "Notary Public (same day)", d: "Notarial Services Attorney + Power of Attorney", to: "/en/notary" as const },
              { t: "Marriage Certificate Translation", d: "Z-Visa, OCI, Iqama, spouse visa", to: "/en/apostille/marriage-certificate" as const },
              { t: "Police Clearance Translation", d: "Saudi, Kuwait, UAE, Qatar, China dossiers", to: "/en/apostille/police-clearance" as const },
              { t: "Work Permit + BOI Visa", d: "For JP/CN/KR engineers in Thailand", to: "/en/work-permit" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
          <QuotableStatsStrip cluster="mfa" locale="en" variant="light" />
          <HubCountryLinks service="mfa-legalisation" lang="en" />
      <ServicePageEnhancer serviceName="MFA Legalisation" locale="en" />
<RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
